import { Link } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';
import type { NavAction, NavItem } from '@/app/nav';
import MotorPlanLogo from '@shared/components/MotorPlanLogo';
import { buttonClassName } from '@shared/components/ui/button/buttonClasses';
import { Tooltip } from '@shared/components/ui/tooltip/Tooltip';
import { hasActiveSubItem } from './activeNavPath';
import RailFlyout from './RailFlyout';
import type { SidebarGroup } from './useSidebarNav';
import { submenuKey, type MenuType } from './useSidebarSubmenu';
import { useRailFlyout } from './useRailFlyout';

interface SidebarRailProps {
    groups: SidebarGroup[];
    isActive: (path: string) => boolean;
    newQuote: NavAction | null;
}

interface RailNavItemProps {
    nav: NavItem;
    menuType: MenuType;
    isActive: (path: string) => boolean;
    flyout: ReturnType<typeof useRailFlyout>;
}

const iconClass = (active: boolean) =>
    `menu-item-icon-size ${active ? 'menu-item-icon-active' : 'menu-item-icon-inactive'}`;

function RailNavItem({ nav, menuType, isActive, flyout }: RailNavItemProps) {
    const key = submenuKey(menuType, nav.name);
    const labelId = `sidebar-rail-label-${key}`;

    if (nav.subItems) {
        const isOpen = flyout.openKey === key;
        const highlighted = isOpen || hasActiveSubItem(nav, isActive);
        const flyoutId = `sidebar-flyout-${key}`;
        return (
            <li className="flex" {...flyout.itemHandlers(key)}>
                <Tooltip content={nav.name} id={labelId} placement="right" disabled={isOpen}>
                    <button
                        type="button"
                        ref={flyout.triggerRef(key)}
                        aria-expanded={isOpen}
                        aria-controls={flyoutId}
                        aria-labelledby={labelId}
                        onClick={() => flyout.toggle(key)}
                        className={`menu-item group cursor-pointer ${highlighted ? 'menu-item-parent-active' : 'menu-item-inactive'}`}
                    >
                        <span className={iconClass(highlighted)}>{nav.icon}</span>
                    </button>
                </Tooltip>
                {isOpen && (
                    <RailFlyout
                        id={flyoutId}
                        ref={flyout.panelRef}
                        title={nav.name}
                        items={nav.subItems}
                        isActive={isActive}
                        top={flyout.flyoutTop}
                        maxHeight={flyout.flyoutMaxHeight}
                    />
                )}
            </li>
        );
    }

    if (!nav.path) return null;
    const active = isActive(nav.path);
    return (
        <li className="flex">
            <Tooltip content={nav.name} id={labelId} placement="right">
                <Link
                    to={nav.path}
                    aria-labelledby={labelId}
                    className={`menu-item group ${active ? 'menu-item-active' : 'menu-item-inactive'}`}
                >
                    <span className={iconClass(active)}>{nav.icon}</span>
                </Link>
            </Tooltip>
        </li>
    );
}

/** The collapsed sidebar: a separate icon layout, never a squeezed copy of the full one. */
export default function SidebarRail({ groups, isActive, newQuote }: SidebarRailProps) {
    const flyout = useRailFlyout();

    return (
        <div className="flex h-full w-22.5 flex-col px-5">
            <div className="flex py-5">
                <Link to="/" aria-label="MotorPlan home">
                    <MotorPlanLogo showText={false} iconSize="h-7" />
                </Link>
            </div>
            {newQuote && (
                <div className="flex shrink-0">
                    <Tooltip content={newQuote.name} id="sidebar-rail-label-new-quote" placement="right">
                        <Link
                            to={newQuote.path}
                            aria-labelledby="sidebar-rail-label-new-quote"
                            className={buttonClassName({ variant: 'primary', appearance: 'solid', size: 'none', className: 'size-11' })}
                        >
                            <PlusIcon className="size-5" />
                        </Link>
                    </Tooltip>
                </div>
            )}
            <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto" onScroll={() => flyout.close()}>
                <nav>
                    <div className="flex flex-col">
                        {groups.map((group, index) => (
                            <div key={group.type}>
                                <div className="mt-6 mb-2 flex h-4 items-center pl-3">
                                    <h2 className="sr-only">{group.heading}</h2>
                                    {index > 0 && (
                                        <span
                                            aria-hidden="true"
                                            data-testid="sidebar-rail-divider"
                                            className="h-px w-5 bg-mp-border"
                                        />
                                    )}
                                </div>
                                <ul className="flex flex-col gap-1">
                                    {group.items.map((nav) => (
                                        <RailNavItem
                                            key={nav.name}
                                            nav={nav}
                                            menuType={group.type}
                                            isActive={isActive}
                                            flyout={flyout}
                                        />
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </nav>
            </div>
        </div>
    );
}
